
import Vue from 'vue';
import RuleService from '@/services/RuleService';
import TagService from '@/services/TagService';
import UserService from '@/services/UserService';
import { extractErrorMessage } from '@/utils/Util';
import RuleActionDialog from '@/components/entities/rule/RuleActionDialog.vue';
import RuleScopeFiltersCard from '@/components/entities/rule/RuleScopeFiltersCard.vue';
import { formatRuleActionLines, FormattedLine } from './ruleActionFormatter';
import {
  scopeEntityTypeOptions,
  scopeTriggerOptions,
  normalizeScopeFilters,
  hasInvalidRegexPair,
} from './ruleConstants';

export default Vue.extend({
  name: 'RuleCreateDialog',

  components: {
    RuleActionDialog,
    RuleScopeFiltersCard,
  },

  data: (viewModel: any): any => ({
    tags: [] as any[],
    users: [] as any[],
    localDialog: false,
    dismissTimeout: null as ReturnType<typeof setTimeout> | null,

    isValid: true,
    isLoading: false,

    alertText: '',
    isAlertVisible: false,
    alertType: 'error',

    rule: {
      name: '',
      description: '',
      scopeEntityType: '',
      scopeTriggers: [] as string[],
      scopeFilters: {} as any,
      actions: [] as any[],
      applyToExisting: true,
    },

    scopeEntityTypeOptions,
    scopeTriggerOptions,

    requiredFieldRules: [
      (value: string) => !!value || (viewModel.$t('required') as string),
    ],
    requiredEntityTypeRules: [
      (value: string) => !!value || (viewModel.$t('required') as string),
    ],

    requiredTriggersRules: [
      (selectedTriggers: string[]) =>
        (!!selectedTriggers && selectedTriggers.length > 0) ||
        (viewModel.$t('rule.atLeastOneTrigger') as string),
    ],
  }),

  methods: {
    validate() {
      if (this.createForm && this.createForm.validate()) {
        if (hasInvalidRegexPair(this.rule.scopeFilters)) {
          this.alertType = 'error';
          this.alertText = this.$t('rule.regexPairError') as string;
          this.isAlertVisible = true;
          return;
        }

        this.createRule();
      }
    },

    close() {
      this.localDialog = false;
    },

    clear() {
      this.rule = {
        name: '',
        description: '',
        scopeEntityType: '',
        scopeTriggers: [],
        scopeFilters: {},
        actions: [],
        applyToExisting: true,
      };
      this.isAlertVisible = false;
      this.isLoading = false;
    },

    dismiss() {
      this.clear();
      this.close();
    },

    addAction(actionItem: any) {
      this.rule.actions.push(actionItem);
    },

    updateAction(actionIndex: number, actionItem: any) {
      this.rule.actions.splice(actionIndex, 1, actionItem);
    },

    removeAction(actionIndex: number) {
      this.rule.actions.splice(actionIndex, 1);
    },

    formatActionLines(actionItem: any): FormattedLine[] {
      return formatRuleActionLines(actionItem, {
        resolveTag: (tagUuid: string) => {
          const foundTag = this.tags.find(
            (tagItem: any) => tagItem.uuid === tagUuid
          );
          return foundTag ? foundTag.name : tagUuid;
        },
        resolveUser: (userUuid: string) => {
          const foundUser = this.users.find(
            (userItem: any) => userItem.uuid === userUuid
          );
          return foundUser ? foundUser.email : userUuid;
        },
      });
    },

    getActionLineLabel(lineKey: string): string {
      if (lineKey === 'condition') {
        const conditionTitleKey = 'rule.condition.title';
        const conditionTitleTranslation = this.$t(conditionTitleKey) as string;
        return conditionTitleTranslation !== conditionTitleKey
          ? conditionTitleTranslation
          : 'Condition';
      }

      const actionLabelKey = `rule.action.${lineKey}`;
      const actionLabelTranslation = this.$t(actionLabelKey) as string;

      return actionLabelTranslation !== actionLabelKey
        ? actionLabelTranslation
        : lineKey;
    },

    getActionTitle(actionType: string): string {
      const map: Record<string, string> = {
        'add-alert': this.$t('rule.action.addAlert') as string,
        'add-tag': this.$t('rule.action.addTag') as string,
        'add-permission': this.$t('rule.action.addPermission') as string,
      };

      return map[actionType] ?? actionType;
    },

    async createRule() {
      this.isLoading = true;
      this.isAlertVisible = false;

      const tenantId = this.$root.$store.state.session.selectedTenant.uuid;

      const payload: any = {
        name: this.rule.name,
        description: this.rule.description.trim(),
        scopeEntityType: this.rule.scopeEntityType,
        scopeTriggers: this.rule.scopeTriggers,
        scopeFilters: normalizeScopeFilters(this.rule.scopeFilters),
        actions: this.rule.actions,
        applyToExisting: this.rule.applyToExisting,
      };

      try {
        const createdRule = await RuleService.createRule(tenantId, payload);

        this.alertType = 'success';
        this.alertText = this.$t('rule.successCreate') as string;
        this.isAlertVisible = true;

        this.$emit('success', createdRule);

        this.dismissTimeout = setTimeout(() => this.dismiss(), 1500);
      } catch (error: any) {
        this.alertType = 'error';
        this.alertText =
          (this.$t('rule.errorCreate') as string) +
          ' ' +
          extractErrorMessage(error);
        this.isAlertVisible = true;
        this.isLoading = false;
      }
    },

    async loadOptions() {
      try {
        this.tags = await TagService.getAllCollectionPages(this.currentTenant);
        this.users = await UserService.getAllCollectionPages({
          tenant: this.currentTenant,
        });
      } catch (error) {
        console.error('Failed to load tags/users', error);
      }
    },
  },

  beforeDestroy() {
    if (this.dismissTimeout) clearTimeout(this.dismissTimeout);
  },

  watch: {
    localDialog(isOpen: boolean) {
      if (isOpen) this.loadOptions();
      else this.clear();
    },
  },

  computed: {
    createForm(): any {
      return this.$refs.createForm;
    },
    currentTenant(): string {
      return this.$root.$store.state.session.selectedTenant.uuid;
    },
  },
});
